<div
  class="ss-ng-table rounded-xl p-6 dark:bg-ss-darker-grey"
  [ngClass]="whiteBackground ? 'bg-ss-lightest-grey' : 'bg-white'"
>
  <!-- Table Header -->
  <div class="tracking-widest text-ss-brown">
    <!-- You can use either <ss-ng-table-header> or <div slot="header"> -->
    <ng-content select="ss-ng-table-header, div[slot=header]"></ng-content>
  </div>

  <!-- Table Body -->
  @if (!isTableReloading && (_getData$ | async); as data) {
    <div class="ss-ng-table-body">
      <cdk-table
        #table
        [dataSource]="data.content ?? data"
        cdkDropList
        (cdkDropListDropped)="drop($event, data)"
        [multiTemplateDataRows]="secondRowColumnsDef().length > 0 ? true : null"
      >
        @if (selection) {
          <ng-container cdkColumnDef="ssNgSelection">
            <!-- Header Definitions -->
            <cdk-header-cell *cdkHeaderCellDef>
              @if (selection === 'multiple') {
                <ss-checkbox
                  (changeValue)="
                    $event ? toggleAllRows(data.content ?? data) : null
                  "
                  [checked]="
                    selected.hasValue() && isAllSelected(data.content ?? data)
                  "
                ></ss-checkbox>
              }
            </cdk-header-cell>
            <cdk-cell *cdkCellDef="let element">
              @if (selection === 'single') {
                <ss-radio
                  (click)="$event.stopPropagation()"
                  [singleRadio]="true"
                  (changeValue)="$event ? onSelectionChange(element) : null"
                  [checked]="selected.isSelected(element)"
                ></ss-radio>
              } @else {
                <ss-checkbox
                  (click)="$event.stopPropagation()"
                  (changeValue)="onSelectionChange(element)"
                  [checked]="selected.isSelected(element)"
                ></ss-checkbox>
              }
            </cdk-cell>
          </ng-container>
        }

        <!-- For loop if column definitions -->
        @for (columnName of columnsDef; track columnName) {
          <ng-container cdkColumnDef="{{ columnName }}">
            <!-- Header Definitions -->
            <cdk-header-cell
              *cdkHeaderCellDef
              (click)="isSortable(columnName) && sort(columnName)"
              [ngClass]="{
                'sortable-class': isSortable(columnName),
                'sorted-selected':
                  pageableState?.sort?.[0]?.field === columnName,
              }"
            >
              <!-- This container looks for a template within the <ss-ng-table> tags with ssNgHeaderKey="{{propertyName}}" -->

              <!-- Eg: 
              <ss-ng-table>
                <ng-template ssNgHeaderKey="propertyName">
                  <div>Custom Header</div>
                </ng-template>
              </ss-ng-table>
            -->
              <ng-container
                *ngTemplateOutlet="
                  (columnName | tableTemplate: headers) || defaultHeader
                "
              ></ng-container>

              <!-- This is the default header template if none is provided -->
              <ng-template #defaultHeader>
                {{ columnName | camelCaseToHrf | numberToSymbol | uppercase }}
              </ng-template>

              <!-- This is the sort icon -->
              @if (pageableState?.sort?.[0]?.field === columnName) {
                <div
                  class="ml-2 bg-ss-gold text-white w-5 h-5 rounded-full flex flex-col items-center justify-center transition-transform"
                  [ngClass]="{
                    'rotate-180': pageableState?.sort?.[0]?.dir === 'desc',
                  }"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="w-4 h-4"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M12 19.5v-15m0 0l-6.75 6.75M12 4.5l6.75 6.75"
                    />
                  </svg>
                </div>
              }
            </cdk-header-cell>

            <!-- Cell Definitions -->

            <!-- This container is looking for <ss-ng-table> tags with ssNgCellKey="{{propertyName}}" -->
            <!-- You can use the object in the template by defining let-element in you template tag -->

            <!-- Eg: 
            <ss-ng-table>
              <ng-template ssNgCellKey="addressColumn" let-element>
                {{ element.address1 }} + {{ element.address2 }}
              </ng-template>
            </ss-ng-table>
          -->
            <cdk-cell *cdkCellDef="let element">
              <ng-container
                *ngTemplateOutlet="
                  (columnName | tableTemplate: cells) || defaultCell;
                  context: { $implicit: element }
                "
              ></ng-container>

              <!-- This is the default cell.  It is assuming that the column name is a key of the object -->
              <ng-template #defaultCell>{{
                element[columnName] | formateIfDate | nullFormatter
              }}</ng-template>
            </cdk-cell>
          </ng-container>
        }

        <!-- Drag and drop -->
        @if (dragAndDrop) {
          <ng-container cdkColumnDef="dragAndDrop">
            <cdk-header-cell *cdkHeaderCellDef>SORT</cdk-header-cell>
            <cdk-cell *cdkCellDef="let element">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 -960 960 960"
                width="24"
                cdkDrag
                cdkDragLockAxis="y"
                cdkDragHandle
                cdkDragRootElement="cdk-row"
                fill="currentColor"
              >
                <path d="M160-360v-80h640v80H160Zm0-160v-80h640v80H160Z" />
              </svg>
            </cdk-cell>
          </ng-container>
        }

        @for (column of secondRowColumnsDef(); track $index) {
          <ng-container [cdkColumnDef]="column">
            <cdk-cell *cdkCellDef="let element">
              <ng-container
                *ngTemplateOutlet="
                  (column | tableTemplate: cells) || defaultCell;
                  context: { $implicit: element }
                "
              ></ng-container>

              <!-- This is the default cell.  It is assuming that the column name is a key of the object -->
              <ng-template #defaultCell>{{
                element[column] | formateIfDate | nullFormatter
              }}</ng-template>
            </cdk-cell>
          </ng-container>
        }

        <!-- Header and Row Declarations -->
        <cdk-header-row *cdkHeaderRowDef="_columnsDef"></cdk-header-row>
        <cdk-row
          [ngClass]="rowNgClassFn && rowNgClassFn(row)"
          [ngStyle]="{ cursor: rowIsListening ? 'pointer' : 'default' }"
          *cdkRowDef="let row; columns: _columnsDef"
          (mousedown)="onRowMouseDown($event)"
          (click)="onRowClick($event, row)"
        ></cdk-row>
        @if (secondRowColumnsDef().length > 0) {
          <cdk-row
            *cdkRowDef="let row; columns: secondRowColumnsDef()"
          ></cdk-row>
        }

        <!-- Empty Table Placeholder -->
        <div
          *cdkNoDataRow
          class="py-4 text-center text-ss-muted-grey font-bold tracking-wider"
        >
          No Results
        </div>
      </cdk-table>

      <!-- Pagination -->
      @if (paging) {
        <div
          class="flex flex-row justify-between border-t border-ss-light-grey px-4 py-2"
        >
          <div class="text-ss-muted-grey my-auto">
            @if (!hidePaginationNumbers) {
              Showing {{ data.number * data.size + 1 }} -
              {{
                data.last
                  ? data.number * data.size + data.content.length
                  : (data.number + 1) * data.size
              }}
              of {{ data.totalElements }}
            }
          </div>
          <div class="flex flex-row">
            <!-- page number of text -->

            <div class="text-ss-muted-grey my-auto mr-4">
              @if (!hidePaginationNumbers) {
                Page {{ data.number + 1 }} of {{ data.totalPages }}
              }
            </div>

            <!-- first icon button-->

            <button
              class="ss-ng-paginator-button"
              [disabled]="data.first"
              (click)="pageChange(0)"
              aria-label="First page"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-4 h-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5"
                />
              </svg>
            </button>
            <!-- backwards -->
            <button
              class="ss-ng-paginator-button"
              aria-label="Previous page"
              [disabled]="data.first"
              (click)="pageChange(data.number - 1)"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            <!-- page numbers -->

            <!-- forwards -->
            <button
              type="button"
              aria-label="Next page"
              class="ss-ng-paginator-button"
              [disabled]="data.last"
              (click)="pageChange(data.number + 1)"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
            <button
              class="ss-ng-paginator-button"
              (click)="pageChange(data.totalPages - 1)"
              [disabled]="data.last"
              aria-label="Last page"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-4 h-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5"
                />
              </svg>
            </button>
          </div>
        </div>
      }
    </div>
  } @else {
    <!-- Skeleton Loader -->
    <div
      class="p-4 border border-gray-300 divide-y divide-gray-300 rounded shadow animate-pulse -700 md:p-6 -700"
    >
      <div class="flex items-center justify-between py-4">
        <div>
          <div class="h-2.5 bg-gray-300 rounded-full w-24 mb-2.5"></div>
          <div class="w-32 h-2 bg-gray-200 rounded-full"></div>
        </div>
        <div class="h-2.5 bg-gray-300 rounded-full w-12"></div>
      </div>
      <div class="flex items-center justify-between py-4">
        <div>
          <div class="h-2.5 bg-gray-300 rounded-full w-24 mb-2.5"></div>
          <div class="w-32 h-2 bg-gray-200 rounded-full"></div>
        </div>
        <div class="h-2.5 bg-gray-300 rounded-full w-12"></div>
      </div>
      <div class="flex items-center justify-between py-4">
        <div>
          <div class="h-2.5 bg-gray-300 rounded-full w-24 mb-2.5"></div>
          <div class="w-32 h-2 bg-gray-200 rounded-full"></div>
        </div>
        <div class="h-2.5 bg-gray-300 rounded-full w-12"></div>
      </div>
      <span class="sr-only">Loading...</span>
    </div>
  }

  <!-- Table footer -->
  <div class="ss-ng-table-footer">
    <!-- You can use either <ss-ng-table-footer> or <div slot="footer"> -->
    <ng-content select="ss-ng-table-footer, div[slot=footer]"></ng-content>
  </div>
</div>
